type Entry<T> = {
  readonly at: number;
  readonly value?: T;
  readonly promise?: Promise<T>;
};

const cache = new Map<string, Entry<unknown>>();

/**
 * De-duplicates concurrent requests and serves fresh results from memory.
 *
 * Without this, React StrictMode's double-invoked effects in development fire
 * two network requests per hook, and any second consumer of the same endpoint
 * would fire its own. Callers share one in-flight promise instead.
 */
export function cachedFetch<T>(key: string, ttlMs: number, loader: () => Promise<T>): Promise<T> {
  const existing = cache.get(key) as Entry<T> | undefined;

  if (existing?.promise) {
    return existing.promise;
  }

  if (existing && existing.value !== undefined && Date.now() - existing.at < ttlMs) {
    return Promise.resolve(existing.value);
  }

  const promise = loader()
    .then((value) => {
      cache.set(key, { at: Date.now(), value });
      return value;
    })
    .catch((error: unknown) => {
      // Don't cache failures — the next mount should be free to retry.
      cache.delete(key);
      throw error;
    });

  cache.set(key, { at: Date.now(), promise });

  return promise;
}
